import { Button, Css, ModalBody, ModalFooter, ModalHeader, SubmitButton, useModal } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useCallback } from "react";
import { LocationPathField } from "src/components/locationPathField/LocationPathField";
import { TakeoffsStoreState } from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";
import { StoreApi } from "zustand";
import {
  ActionRadioField,
  AddEditItemModalDataViewProps,
  AddEditItemObjectState,
  CommonFields,
  OnTaskSelect,
  PublishedTakeoffBanner,
  TakeoffTaskBoundSelectField,
  TliData,
  saveAddEditItem,
  useAddEditItemModalFormState,
  withGetTliQuery,
} from "./utils";

export const AddEditLaborItemModal = withGetTliQuery(AddEditLaborItemModalDataView);

function AddEditLaborItemModalDataView(props: AddEditItemModalDataViewProps) {
  const { planPackageId, initialTliData, takeoffsManagerStore } = props;

  const { saveTli, planPackage } = takeoffsManagerStore.getState();
  const { closeModal, addCanClose } = useModal();
  const isEditing = !!initialTliData?.id;
  const readOnly = !planPackage.canEditLineItems.allowed;

  const formState = useAddEditItemModalFormState("labor", readOnly, initialTliData);

  addCanClose(() => {
    return formState.dirty ? window.confirm("You have unsaved changes. Are you sure you want to close?") : true;
  });

  return (
    <>
      <ModalHeader>
        <span css={Css.lg.$}>{readOnly ? "Labor Details" : isEditing ? "Edit Labor" : "Add Labor"}</span>
      </ModalHeader>
      <ModalBody>
        {readOnly && <PublishedTakeoffBanner />}
        <div css={Css.df.fdc.gap2.pb3.$}>
          <LaborFields
            store={takeoffsManagerStore}
            planPackageId={planPackageId}
            initialTliData={initialTliData}
            readOnly={readOnly}
            formState={formState}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        {readOnly ? (
          <Button label="Close" onClick={closeModal} />
        ) : (
          <>
            <Button variant="quaternary" label="Cancel" onClick={closeModal} size="lg" />
            <SubmitButton
              label="Save"
              data-testid="saveBtn"
              form={formState}
              onClick={async () => {
                await saveAddEditItem({ formState, saveTli });
                closeModal();
              }}
              size="lg"
            />
          </>
        )}
      </ModalFooter>
    </>
  );
}

type LaborFieldProps = {
  store: StoreApi<TakeoffsStoreState>;
  formState: ObjectState<AddEditItemObjectState>;
  planPackageId: string;
  initialTliData?: TliData;
  readOnly: boolean;
};

function LaborFields({ store, planPackageId, initialTliData, readOnly, formState }: LaborFieldProps) {
  const onTaskSelect: OnTaskSelect = useCallback(
    (id: string | undefined) => {
      formState.taskId.set(id);
    },
    [formState],
  );

  return (
    <>
      <ActionRadioField field={formState.action} readOnly={readOnly} />
      <TakeoffTaskBoundSelectField
        required
        selectedTask={initialTliData?.task}
        readOnly={readOnly}
        taskId={formState.taskId}
        onSelect={onTaskSelect}
      />
      <LocationPathField
        filter={{ rpav: [store.getState().planPackage.version.id] }}
        newLocationPathField={formState.newLocationPath}
        field={formState.locationId}
        onSelect={(_locationId, location) => formState.location.set(location)}
      />
      <CommonFields
        planPackageId={planPackageId}
        planPackageVersionId={store.getState().planPackage.version.id}
        readOnly={readOnly}
        formState={formState}
      />
    </>
  );
}
